import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckSquare,
  Loader2,
  PenLine,
  Play,
  Rocket,
  UserRound,
  Users,
  Video,
} from 'lucide-react';
import { ApiError, publicApi, type MasterDataItem } from '../lib/apiClient';
import { textError, emailError, urlError } from '../lib/publicFormValidation';
import { usePageTitle } from '../lib/usePageTitle';
import { mapPublicResource, normalizeResourceType, resourceVideoSource, type ResourceItem, type ResourceType } from './resourcesData';
import './resources.css';


const PERSONAS = [
  { icon: Users, title: 'Job seekers', text: "What's changing in interviews, comp, and how to actually stand out" },
  { icon: CheckSquare, title: 'Hiring managers', text: "Real benchmarks on time-to-shortlist, offer stages, and what's slowing you down" },
  { icon: UserRound, title: 'TA leaders', text: 'How other functions are scaling process without scaling headcount' },
  { icon: Rocket, title: 'Startup founders', text: "What founding-team hiring actually looks like from people who've done it" },
];

function typeSuffix(resource: ResourceItem) {
  if (resource.type === 'video') return ' · Video';
  if (resource.type === 'story') return ' · Company Story';
  return '';
}

// LinkedIn URL validation: ensures the value is both a valid URL and actually points to linkedin.com
function linkedinUrlError(value: string): string | undefined {
  const trimmed = value.trim();
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return 'Enter a valid LinkedIn URL (e.g. https://linkedin.com/in/username).';
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) {
    return 'Enter a valid LinkedIn URL (e.g. https://linkedin.com/in/username).';
  }
  return undefined;
}

// Ensures the value contains only alphabetic characters (letters and spaces), no digits/symbols
function nameFormatError(value: string, label: string): string | undefined {
  if (!/^[A-Za-z\s]+$/.test(value.trim())) {
    return `${label} should contain only letters.`;
  }
  return undefined;
}

function ResourceCard({ resource, compact = false }: { resource: ResourceItem; compact?: boolean }) {
  const action = resource.type === 'video' ? 'Watch' : resource.type === 'story' ? 'Read the story' : 'Read more';
  const secondary = resource.type === 'video' ? resource.duration : resource.type === 'story' ? 'Company story' : resource.readTime;

  return (
    <Link className={`resource-card${resource.type === 'video' ? ' resource-card--video' : ''}${compact ? ' resource-card--compact' : ''}`} to={`/resources/${resource.slug}`}>
      {resource.type === 'video' && (
        <div className="resource-video-thumb" aria-hidden="true">
          <span className="resource-play"><Play fill="currentColor" /></span>
          <span className="resource-duration">{resource.duration}</span>
        </div>
      )}
      {resource.type === 'story' && (
        <div className="resource-stat-block">
          <strong>{resource.stat}</strong>
          <span>{resource.statLabel}</span>
        </div>
      )}
      <span className="resource-tag">{resource.category}{typeSuffix(resource)}</span>
      <h3>{resource.title}</h3>
      <p>{resource.excerpt}</p>
      <div className="resource-author">
        <span className="resource-avatar" style={{ backgroundColor: resource.author.color }}>
          {resource.author.photoUrl ? <img src={resource.author.photoUrl} alt="" /> : resource.author.initials}
        </span>
        <span>
          <span className="resource-author-name">
            {resource.author.name}
            {resource.author.kind === 'guest' && <em>Guest</em>}
          </span>
          {resource.author.company && <small>{resource.author.company}</small>}
        </span>
      </div>
      <div className="resource-meta"><span>{resource.publishedAt}</span><span>{secondary}</span></div>
      <span className="resource-link">{action} <ArrowRight /></span>
    </Link>
  );
}

interface SuggestForm {
  nomineeName: string;
  nomineeTitle: string;
  pitch: string;
  name: string;
  email: string;
  linkedin: string;
}

const EMPTY_SUGGESTION: SuggestForm = {
  nomineeName: '',
  nomineeTitle: '',
  pitch: '',
  name: '',
  email: '',
  linkedin: '',
};

function SuggestVoiceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState(EMPTY_SUGGESTION);
  const [errors, setErrors] = useState<Partial<Record<keyof SuggestForm, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const firstInput = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_SUGGESTION);
    setErrors({});
    setSubmitError(null);
    setSubmitted(false);
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => firstInput.current?.focus(), 0);
    const handleKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', handleKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (key: keyof SuggestForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof SuggestForm, string>> = {};
    next.nomineeName = textError(form.nomineeName, 'Their name', 2, 120);
    next.nomineeName ||= nameFormatError(form.nomineeName, 'Their name');
    if (form.nomineeTitle.trim().length < 2) next.nomineeTitle = 'Enter their title and company.';
    if (form.pitch.trim().length < 10) next.pitch = 'Tell us a bit about why — a sentence or two is enough.';
    next.nomineeTitle ||= textError(form.nomineeTitle, 'Title and company', 2, 255);
    next.pitch ||= textError(form.pitch, 'Reason', 10, 2000);
    next.name = textError(form.name, 'Your name', 2, 120);
    next.name ||= nameFormatError(form.name, 'Your name');
    next.email = emailError(form.email);
    next.linkedin = form.linkedin.trim() ? (urlError(form.linkedin, 2000) || linkedinUrlError(form.linkedin)) : undefined;
    Object.keys(next).forEach((key) => next[key as keyof SuggestForm] === undefined && delete next[key as keyof SuggestForm]);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) {
      window.requestAnimationFrame(() => {
        formRef.current
          ?.querySelector<HTMLInputElement | HTMLTextAreaElement>('[aria-invalid="true"]')
          ?.focus();
      });
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await publicApi.submitFeatureNomination({
        person_name: form.nomineeName.trim(),
        person_title_company: form.nomineeTitle.trim(),
        reason: form.pitch.trim(),
        submitted_by_name: form.name.trim(),
        submitted_by_email: form.email.trim(),
        linkedin_url: form.linkedin.trim() || undefined,
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : 'Could not send the suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="resource-modal" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="resource-modal-card" role="dialog" aria-modal="true" aria-labelledby="suggest-voice-title">
        <button className="resource-modal-close" type="button" onClick={onClose} aria-label="Close">✕</button>
        {submitted ? (
          <div className="resource-modal-success">
            <span>✓</span>
            <h3>Got it — thanks for the nomination.</h3>
            <p>We&apos;ll take a look and reach out to them directly if it&apos;s a fit for a guest article or interview.</p>
          </div>
        ) : (
          <>
            <span className="resource-modal-eyebrow">// Featured voices</span>
            <h3 id="suggest-voice-title">Suggest someone to feature</h3>
            <p className="resource-modal-sub">Know a founder or talent leader with a hiring story worth featuring? Tell us who — we&apos;ll reach out directly.</p>
            <form ref={formRef} onSubmit={submit} noValidate>
              <div className="resource-form-row">
                <label>Their name<input ref={firstInput} value={form.nomineeName} onChange={(e) => update('nomineeName', e.target.value)} aria-invalid={Boolean(errors.nomineeName)} placeholder="e.g. Priya Nambiar" />{errors.nomineeName && <small>{errors.nomineeName}</small>}</label>
                <label>Their title & company<input value={form.nomineeTitle} onChange={(e) => update('nomineeTitle', e.target.value)} aria-invalid={Boolean(errors.nomineeTitle)} placeholder="VP People, Acme Inc." />{errors.nomineeTitle && <small>{errors.nomineeTitle}</small>}</label>
              </div>
              <label>Why are they worth featuring?<textarea value={form.pitch} onChange={(e) => update('pitch', e.target.value)} aria-invalid={Boolean(errors.pitch)} placeholder="e.g. She rebuilt their entire hiring process after a bad scale-up phase — great story on what she'd do differently." />{errors.pitch && <small>{errors.pitch}</small>}</label>
              <div className="resource-form-row">
                <label>Your name<input value={form.name} onChange={(e) => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} placeholder="Jordan Patel" />{errors.name && <small>{errors.name}</small>}</label>
                <label>Your email<input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} placeholder="jordan@company.com" />{errors.email && <small>{errors.email}</small>}</label>
              </div>
              <label>
                <span>Their LinkedIn URL <span className="resource-optional">(optional)</span></span>
                <input
                  type="url"
                  value={form.linkedin}
                  aria-invalid={Boolean(errors.linkedin)}
                  onChange={(e) => update('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/theirname"
                />
                {errors.linkedin && <small>{errors.linkedin}</small>}
              </label>
              {submitError && <p className="resource-submit-error" role="alert">{submitError}</p>}
              <button className="resource-submit" type="submit" disabled={submitting}>{submitting ? <><Loader2 className="spin" /> Sending…</> : 'Send the nomination →'}</button>
              <p className="resource-form-note">Nominating yourself? That&apos;s fine too — just tell us why.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export function ResourcesPage() {
  const [type, setType] = useState<'All' | ResourceType>('All');
  const [category, setCategory] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [resourceTypes, setResourceTypes] = useState<MasterDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  usePageTitle('Resources — Industry News & Hiring Insights | QuantumHire');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);

    const loadResources = async () => {
      try {
        const [first, types] = await Promise.all([
          publicApi.listResources(),
          publicApi.resourceTypes(),
        ]);
        const pages = [first];
        for (let page = 2; page <= first.meta.total_pages; page += 1) {
          pages.push(await publicApi.listResources(page));
        }
        if (active) {
          setResources(pages.flatMap((result) => result.data).map(mapPublicResource));
          setResourceTypes(types.map((item) => ({ ...item, slug: normalizeResourceType(item.slug) })).filter((item, index, items) => items.findIndex((other) => other.slug === item.slug) === index));
          setType((current) => (
            current === 'All' || types.some((resourceType) => normalizeResourceType(resourceType.slug) === current)
              ? current
              : 'All'
          ));
        }
      } catch (error) {
        if (active) setLoadError(error instanceof ApiError ? error.message : 'Could not load resources. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadResources();
    return () => { active = false; };
  }, [retry]);

  const categories = useMemo(() => ['All', ...new Set(resources.map((resource) => resource.category))], [resources]);

  const results = useMemo(() => resources.filter((resource) =>
    (type === 'All' || resource.type === type) && (category === 'All' || resource.category === category),
  ), [category, resources, type]);

  return (
    <main className="resources-page">
      <header className="resources-hero">
        {[0, 1, 2, 3].map((particle) => <span className={`resources-particle resources-particle--${particle + 1}`} key={particle} />)}
        <div className="resources-hero-copy">
          <span className="resources-eyebrow"><i /> The knowledge hub for anyone in hiring</span>
          <h1>What&apos;s actually happening<br />in hiring right now.</h1>
          <p>Whether you&apos;re job hunting, running a hiring team, leading TA at scale, or building your first team as a founder — this is where we put what we&apos;re actually seeing across four markets, so you don&apos;t have to guess.</p>
        </div>
        <div className="resources-personas">
          {PERSONAS.map(({ icon: Icon, title, text }) => <article key={title}><span><Icon /></span><h2>{title}</h2><p>{text}</p></article>)}
        </div>
      </header>

      <section className="resources-listings" aria-labelledby="resources-list-title">
        <h2 className="sr-only" id="resources-list-title">Resource library</h2>
        <div className="resources-chips" aria-label="Filter by resource type">
          <button type="button" className={type === 'All' ? 'active' : ''} aria-pressed={type === 'All'} onClick={() => setType('All')}>All content</button>
          {resourceTypes.map((option) => <button type="button" key={option.id} className={type === option.slug ? 'active' : ''} aria-pressed={type === option.slug} onClick={() => setType(option.slug)}>{option.name}</button>)}
        </div>
        <div className="resources-chips resources-chips--categories" aria-label="Filter by category">
          {categories.map((option) => <button type="button" key={option} className={category === option ? 'active' : ''} aria-pressed={category === option} onClick={() => setCategory(option)}>{option}</button>)}
        </div>
        {loading ? (
          <p className="resources-empty"><Loader2 className="spin" aria-hidden="true" /> Loading resources…</p>
        ) : loadError ? (
          <div className="resources-empty" role="alert"><p>{loadError}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>Try again</button></div>
        ) : results.length ? (
          <div className="resources-grid">{results.map((resource) => <ResourceCard resource={resource} key={resource.slug} />)}</div>
        ) : (
          <p className="resources-empty">{resources.length ? 'No resources match those filters.' : 'No resources have been published yet.'}</p>
        )}
      </section>

      <section className="featured-voices">
        <div className="featured-voices-inner">
          <div className="featured-voices-copy">
            <span>// Featured voices</span>
            <h2>We&apos;re featuring founders and talent leaders doing hiring right.</h2>
            <p>Every month we sit down with people building genuinely good hiring functions — a smart call they made, a mistake they&apos;d flag, a lesson worth passing on. If you know someone worth that conversation, tell us who.</p>
            <div className="featured-options">
              <article><i><PenLine /></i><h3>Guest articles</h3><p>Written pieces from people we&apos;ve invited — a real lesson, under their own name.</p></article>
              <article><i><Video /></i><h3>Video interviews</h3><p>Short, honest conversations with founders and talent leaders we reach out to directly.</p></article>
            </div>
          </div>
          <button type="button" onClick={() => setModalOpen(true)}>Suggest someone to feature <ArrowRight /></button>
        </div>
      </section>
      <SuggestVoiceModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}

type Attributes = Record<string, unknown>;
interface Line { children: ReactNode[]; attributes: Attributes; italic?: boolean; quoteLines?: ReactNode[][] }

function safeLink(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value, 'https://quantumhire.ai');
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

function inline(text: string, attributes: Attributes): ReactNode {
  let node: ReactNode = text.includes('\u2028')
    ? text.split('\u2028').map((part, index) => <Fragment key={index}>{index > 0 && <br />}{part}</Fragment>)
    : text;
  if (attributes.bold) node = <strong>{node}</strong>;
  if (attributes.italic) node = <em>{node}</em>;
  if (attributes.underline) node = <u>{node}</u>;
  const href = safeLink(attributes.link);
  if (href) node = <a href={href}>{node}</a>;
  return node;
}

function contentLines(content: unknown): Line[] {
  // Keep legacy plain-text resources readable alongside saved Quill Deltas.
  if (typeof content === 'string' || Array.isArray(content)) {
    const paragraphs = typeof content === 'string' ? content.split(/\n\s*\n/) : content;
    return paragraphs.filter((item): item is string => typeof item === 'string')
      .map((text) => ({ children: [text], attributes: {} }));
  }
  if (!content || typeof content !== 'object') return [];
  const ops = (content as { ops?: unknown }).ops;
  if (!Array.isArray(ops)) return [];
  const lines: Line[] = [];
  let children: ReactNode[] = [];
  let quoteLines: ReactNode[][] = [[]];
  let italic = true;
  for (const op of ops) {
    if (!op || typeof op.insert !== 'string') continue;
    const attributes: Attributes = op.attributes && typeof op.attributes === 'object' ? op.attributes : {};
    const parts = op.insert.split('\n');
    parts.forEach((part: string, index: number) => {
      if (index > 0) {
        // Quill stores block formatting on the newline that ends the line.
        lines.push({ children, attributes, italic, quoteLines });
        children = [];
        quoteLines = [[]];
        italic = true;
      }
      if (part) {
        children.push(<Fragment key={children.length}>{inline(part, attributes)}</Fragment>);
        // Track soft breaks for quote presentation without changing Delta blocks.
        part.split('\u2028').forEach((text, softIndex) => {
          if (softIndex > 0) quoteLines.push([]);
          const runs = quoteLines[quoteLines.length - 1];
          if (text) runs.push(<Fragment key={runs.length}>{inline(text, attributes)}</Fragment>);
        });
        if (part.trim() && (!attributes.italic || attributes.bold)) italic = false;
      }
    });
  }
  if (children.length) lines.push({ children, attributes: {} });
  return lines;
}

function ResourceContent({ content, type }: { content: unknown; type: ResourceType }) {
  const isCaseStudy = ['case-study', 'case-studies'].includes(type.trim().toLowerCase().replace(/[\s_]+/g, '-'));
  const lines = contentLines(content);
  const blocks: ReactNode[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const children = line.children.length ? line.children : <br />;
    const list = line.attributes.list;
    if (list === 'ordered' || list === 'bullet') {
      const items: ReactNode[] = [];
      const start = index;
      while (index < lines.length && lines[index].attributes.list === list) {
        items.push(<li key={index}>{lines[index].children.length ? lines[index].children : <br />}</li>);
        index += 1;
      }
      index -= 1;
      blocks.push(list === 'ordered' ? <ol key={start}>{items}</ol> : <ul key={start}>{items}</ul>);
    } else if (line.attributes.header === 1) blocks.push(<h1 key={index}>{children}</h1>);
    else if (line.attributes.header === 2) blocks.push(<h2 key={index}>{children}</h2>);
    else if (line.attributes.header === 3) blocks.push(<h3 key={index}>{children}</h3>);
    else if (line.attributes.header === 4) blocks.push(<h4 key={index}>{children}</h4>);
    else if (line.attributes.header === 5) blocks.push(<h5 key={index}>{children}</h5>);
    else if (line.attributes.header === 6) blocks.push(<h6 key={index}>{children}</h6>);
    else if (line.attributes.blockquote) {
      const start = index;
      const quoteLines = [...(line.quoteLines ?? [line.children])];
      let founderQuote = isCaseStudy && line.attributes.founderQuote === true;
      // Older case studies saved italic quote lines and attribution as separate blocks.
      const legacyQuote = (item: Line) => item.attributes.blockquote
        && !item.attributes.founderQuote && !item.attributes.header && !item.attributes.list;
      if (isCaseStudy && legacyQuote(line) && line.italic) {
        let details = index + 1;
        while (details < lines.length && legacyQuote(lines[details]) && lines[details].italic) details++;
        let end = details;
        while (end < lines.length && legacyQuote(lines[end]) && !lines[end].italic) end++;
        if (end > details) {
          founderQuote = true;
          for (let next = index + 1; next < end; next++) quoteLines.push(...(lines[next].quoteLines ?? [lines[next].children]));
          index = end - 1;
        }
      }
      // Consecutive Quill quote lines form one quote, including Shift+Enter breaks.
      // Keep dedicated founder blocks separate from ordinary body quotes.
      if (!founderQuote && legacyQuote(line)) {
        while (index + 1 < lines.length && legacyQuote(lines[index + 1])) {
          index += 1;
          quoteLines.push(...(lines[index].quoteLines ?? [lines[index].children]));
        }
      }
      const attribution = quoteLines.length > 1 ? quoteLines.pop() : undefined;
      blocks.push(<blockquote key={start} className={founderQuote ? 'resource-founder-quote' : undefined}>
        <p>{quoteLines.map((text, part) => <Fragment key={part}>{part > 0 && <br />}{text}</Fragment>)}</p>
        {attribution && <cite>{attribution}</cite>}
      </blockquote>);
    }
    else blocks.push(<p key={index}>{children}</p>);
  }
  return <div className="resource-rich-text">{blocks}</div>;
}

function ResourceVideo({ resource }: { resource: ResourceItem }) {
  const source = resourceVideoSource(resource.videoUrl);
  const [failed, setFailed] = useState(false);
  if (!source || failed) return <p className="resource-video-player" role="status">This video is currently unavailable.</p>;
  return source.embed ? (
    <iframe className="resource-video-player" src={source.src} title={`Watch ${resource.title}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
  ) : (
    <video className="resource-video-player" src={source.src} controls playsInline preload="metadata" aria-label={`Watch ${resource.title}`} onError={() => setFailed(true)} />
  );
}

export function ResourceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [resource, setResource] = useState<ResourceItem | null>(null);
  const [related, setRelated] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [retry, setRetry] = useState(0);
  usePageTitle(resource ? `${resource.title} — QuantumHire` : 'Resources — QuantumHire');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    setNotFound(false);

    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return () => { active = false; };
    }

    publicApi.getResource(slug)
      .then((result) => {
        if (!active) return;
        setResource(mapPublicResource(result));
        setRelated((result.related_resources ?? []).map(mapPublicResource));
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 404) setNotFound(true);
        else setLoadError(error instanceof ApiError ? error.message : 'Could not load this resource. Please try again.');
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [retry, slug]);

  if (notFound) return <Navigate to="/resources" replace />;
  if (loading) return <main className="resource-detail"><p className="resources-empty"><Loader2 className="spin" aria-hidden="true" /> Loading resource…</p></main>;
  if (loadError || !resource) return <main className="resource-detail"><div className="resources-empty" role="alert"><p>{loadError ?? 'Could not load this resource.'}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>Try again</button></div></main>;

  return (
    <main className="resource-detail">
      <header className="resource-detail-hero">
        <div>
          <Link className="resource-back" to="/resources">← Back to Resources</Link>
          <span className="resource-tag">{resource.category}{typeSuffix(resource)}</span>
          <h1>{resource.title}</h1>
          <div className="resource-detail-author">
            <span className="resource-avatar" style={{ backgroundColor: resource.author.color }}>{resource.author.photoUrl ? <img src={resource.author.photoUrl} alt="" /> : resource.author.initials}</span>
            <span><strong>{resource.author.name}{resource.author.kind === 'guest' ? ' (Guest)' : ''}</strong><small>{resource.author.title}{resource.author.company ? `, ${resource.author.company}` : ''}</small></span>
            <span className="resource-detail-meta"><span>{resource.publishedAt}</span><span>{resource.duration ?? (resource.type === 'story' ? 'Company story' : resource.readTime)}</span></span>
          </div>
        </div>
      </header>
      <article className="resource-article-body">
        {resource.type === 'video' && <ResourceVideo key={resource.videoUrl ?? resource.slug} resource={resource} />}
        {resource.type === 'story' && <div className="resource-story-highlight"><strong>{resource.stat}</strong><span>{resource.statLabel}</span></div>}
        <ResourceContent content={resource.body} type={resource.type} />
      </article>
      <div className="resource-detail-actions"><Link to="/resources">← All articles</Link><Link to="/employers">Talk to us about a role →</Link></div>
      {related.length > 0 && <section className="resource-related"><div><h2>// More from Resources</h2><div>{related.map((item) => <ResourceCard resource={item} compact key={item.slug} />)}</div></div></section>}
    </main>
  );
}
